import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchIncomeStatement, type IncomeStatementResponse } from '../incomeStatement/incomeStatementApi';
import { fetchCompanyBudgetWorkspace, type CompanyBudgetWorkspaceDto } from '../companyBudget/companyBudgetApi';
import { fetchLiquidityReport, type LiquidityReport } from './liquidityApi';
import { fetchOrders, fetchOrdersWithRows } from '../../orders/api/ordersApi';
import type { DashboardOrder } from './ordersModel';

// Loads the read models of the "Pulpit zarządu" for one year. Each source
// fails independently: a missing source is `null` + its error flag, never an
// empty dataset (an unloaded collection is not an empty one).

export interface DashboardData {
  statement: IncomeStatementResponse | null;
  budget: CompanyBudgetWorkspaceDto | null;
  liquidity: LiquidityReport | null;
  orders: DashboardOrder[] | null;
  /**
   * The same order list as sent by the API (one request with `orders`): the
   * page's sales cut and „Wymaga uwagi” read products and the Opiekun from it.
   */
  orderRows: unknown[] | null;
  quotes: DashboardOrder[] | null;
  errors: { statement: boolean; budget: boolean; liquidity: boolean; orders: boolean };
  loading: boolean;
  reloadBudget: () => void;
  /** After a typed cash balance (Płynność). */
  reloadLiquidity: () => void;
  /**
   * After a ZK change that moves the result (a dragged forecast month,
   * 2026-10-30): refetch the income statement and the order list in place —
   * the old data stays on screen until the new arrives (no spinner). Rejects
   * when the statement could not be read again.
   */
  reloadResult: () => Promise<void>;
}

function toDashboardOrders(list: Awaited<ReturnType<typeof fetchOrders>>): DashboardOrder[] {
  return list.map((order) => ({
    id: order.id,
    orderNumber: order.orderNumber,
    manualOrderNumber: order.manualOrderNumber,
    useManualNumbering: order.useManualNumbering,
    status: order.status,
    customer: order.customer ? { id: order.customer.id, name: order.customer.name } : undefined,
    revenue: Number(order.revenue) || 0,
    totalCost: order.totalCost == null ? null : Number(order.totalCost),
    margin: order.margin == null ? null : Number(order.margin),
    marginPercent: order.marginPercent == null ? null : Number(order.marginPercent),
    warehouseCostPending: order.warehouseCostPending,
    orderDate: order.orderDate,
    effectiveForecastMonth: order.effectiveForecastMonth,
    expectedDelivery: order.expectedDelivery,
  }));
}

export function useDashboardData(year: number): DashboardData {
  const [statement, setStatement] = useState<IncomeStatementResponse | null>(null);
  const [budget, setBudget] = useState<CompanyBudgetWorkspaceDto | null>(null);
  const [liquidity, setLiquidity] = useState<LiquidityReport | null>(null);
  const [orders, setOrders] = useState<DashboardOrder[] | null>(null);
  const [orderRows, setOrderRows] = useState<unknown[] | null>(null);
  const [quotes, setQuotes] = useState<DashboardOrder[] | null>(null);
  const [errors, setErrors] = useState({ statement: false, budget: false, liquidity: false, orders: false });
  const [pending, setPending] = useState(0);
  const [budgetVersion, setBudgetVersion] = useState(0);
  const [liquidityVersion, setLiquidityVersion] = useState(0);

  const yearRef = useRef(year);
  yearRef.current = year;

  const fail = useCallback((key: keyof DashboardData['errors']) => setErrors((current) => ({ ...current, [key]: true })), []);

  useEffect(() => {
    let cancelled = false;
    setStatement(null);
    setErrors((current) => ({ ...current, statement: false }));
    setPending((count) => count + 1);
    fetchIncomeStatement(`${year}-01`, `${year}-12`)
      .then((response) => { if (!cancelled) setStatement(response); })
      .catch(() => { if (!cancelled) fail('statement'); })
      .finally(() => setPending((count) => count - 1));
    return () => { cancelled = true; };
  }, [year, fail]);

  useEffect(() => {
    let cancelled = false;
    setErrors((current) => ({ ...current, budget: false }));
    fetchCompanyBudgetWorkspace(year)
      .then((response) => { if (!cancelled) setBudget(response); })
      .catch(() => { if (!cancelled) { setBudget(null); fail('budget'); } });
    return () => { cancelled = true; };
  }, [year, budgetVersion, fail]);

  useEffect(() => {
    let cancelled = false;
    setErrors((current) => ({ ...current, liquidity: false }));
    fetchLiquidityReport()
      .then((response) => { if (!cancelled) setLiquidity(response); })
      .catch(() => { if (!cancelled) fail('liquidity'); });
    return () => { cancelled = true; };
  }, [liquidityVersion, fail]);

  const loadOrderLists = useCallback(() => Promise.all([
    fetchOrdersWithRows({ isQuote: false, sortBy: 'orderDate', sortOrder: 'desc' }, 'managementDashboard.orders'),
    fetchOrders({ isQuote: true, sortBy: 'orderDate', sortOrder: 'desc' }, 'managementDashboard.quotes'),
  ]), []);

  useEffect(() => {
    let cancelled = false;
    loadOrderLists()
      .then(([orderList, quoteList]) => {
        if (cancelled) return;
        setOrders(toDashboardOrders(orderList.orders));
        setOrderRows(orderList.rows);
        setQuotes(toDashboardOrders(quoteList));
      })
      .catch(() => { if (!cancelled) fail('orders'); });
    return () => { cancelled = true; };
  }, [fail, loadOrderLists]);

  const reloadResult = useCallback(async () => {
    const requested = year;
    const [response] = await Promise.all([
      fetchIncomeStatement(`${requested}-01`, `${requested}-12`),
      loadOrderLists()
        .then(([orderList, quoteList]) => {
          if (yearRef.current !== requested) return;
          setOrders(toDashboardOrders(orderList.orders));
          setOrderRows(orderList.rows);
          setQuotes(toDashboardOrders(quoteList));
        })
        // The order list is secondary here: the previous one stays.
        .catch(() => undefined),
    ]);
    if (yearRef.current === requested) setStatement(response);
  }, [year, loadOrderLists]);

  return {
    statement,
    budget,
    liquidity,
    orders,
    orderRows,
    quotes,
    errors,
    loading: pending > 0,
    reloadBudget: () => setBudgetVersion((version) => version + 1),
    reloadLiquidity: () => setLiquidityVersion((version) => version + 1),
    reloadResult,
  };
}
